import React, {useState} from "react";
import {Table, Tag, Card, Typography, Row} from "antd";
import moment from "moment";
import {useObserver} from "mobx-react-lite";
import styled from "styled-components";

import {useFetch} from "@vigo/hooks";
import {useStore} from "@vigo/hooks";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import PoolIntensityCallbacksFilters from "./PoolIntensityCallbacksFilters";
import SubCompaniesDetailModal from "./components/SubCompaniesDetailModal";
import {truncateString} from "../../../utils/helpers";

const {Column} = Table;
const {Text} = Typography;

const ExpandedContainer = styled.div`
    background: #fafafa;
    border-radius: 8px;
    margin: 12px 16px;
    padding: 16px;
    border-left: 4px solid #1890ff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const ExpandedTitle = styled.div`
    color: #1890ff;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
        content: "📊";
        font-size: 16px;
    }
`;

const SubTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: #f0f0f0;
        font-weight: 600;
        font-size: 12px;
        padding: 8px 12px;
        border-bottom: 2px solid #d9d9d9;
        text-align: center;
    }

    .ant-table-tbody > tr > td {
        padding: 12px;
        font-size: 13px;
        border-bottom: 1px solid #f0f0f0;
        text-align: center;
    }

    .ant-table-tbody > tr:hover > td {
        background: #e6f7ff;
    }

    .ant-table-tbody > tr:last-child > td {
        border-bottom: none;
    }
`;

const MainTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: #001529;
        color: white;
        font-weight: 600;
        border-bottom: 2px solid #1890ff;
        text-align: center;
    }

    .ant-table-tbody > tr {
        transition: all 0.3s ease;
    }

    .ant-table-tbody > tr > td {
        text-align: center;
    }

    .ant-table-tbody > tr:hover > td {
        background: #f6ffed;
    }

    .ant-table-expand-icon-col {
        width: 48px;
    }

    .ant-table-row-expand-icon {
        background: #1890ff;
        border: 2px solid #1890ff;
        color: white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        transition: all 0.3s ease;

        &:hover {
            background: #40a9ff;
            transform: scale(1.1);
        }
    }

    .ant-table-row-expand-icon-expanded {
        background: #52c41a;
        border-color: #52c41a;
    }
`;

const PageContainer = styled.div`
    background: #f0f2f5;
`;

const ContentCard = styled(Card)`
    .ant-card-head {
        background: #001529;
        border-bottom: 3px solid #1890ff;

        .ant-card-head-title {
            color: white;
            font-size: 18px;
            font-weight: 600;
        }
    }
`;

const FiltersCard = styled(Card)`
    .ant-card-head {
        background: #fafafa;
        border-bottom: 2px solid #d9d9d9;

        .ant-card-head-title {
            color: #333;
            font-size: 16px;
            font-weight: 600;
        }
    }
`;

const PoolIntensityCallbacks = () => {
    const stores = useStore();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Filter state management
    const [filters, setFilters] = useState({
        poolId: undefined,
        companyId: undefined,
        subCompanyId: undefined
    });

    const [subCompaniesModal, setSubCompaniesModal] = useState({
        open: false,
        subCompanyName: ""
    });

    // API isteği için en az bir filtre seçilmiş olmalı
    const hasValidFilters = filters.poolId || filters.companyId || filters.subCompanyId;

    const [loading, poolIntensityData, setPoolIntensityData] = useFetch({
        defaultDataValue: {data: [], pagination: {}},
        fetchFn: () => {
            const params = {
                poolId: filters.poolId,
                companyId: filters.companyId,
                subCompanyId: filters.subCompanyId,
                page: currentPage,
                limit: pageSize
            };
            return stores.poolStore.getPoolIntensityCallbacks(params);
        },
        dependencies: [
            filters.poolId,
            filters.companyId,
            filters.subCompanyId,
            currentPage,
            pageSize
        ],
        condition: hasValidFilters,
        initialCall: false
    });

    const getTypeLabel = type => {
        switch (type) {
            case "highIntensity":
                return (
                    <Tag color="red" style={{fontWeight: 600}}>
                        🔥 Yüksek Yoğunluk
                    </Tag>
                );
            case "normalIntensity":
                return (
                    <Tag color="blue" style={{fontWeight: 600}}>
                        📈 Normal Yoğunluk
                    </Tag>
                );
            default:
                return <Tag style={{fontWeight: 600}}>{type}</Tag>;
        }
    };

    const expandedRowRender = record => {
        const subColumns = [
            {
                title: "Aktif Kurye Sayısı",
                dataIndex: "activeCouriersCount",
                key: "activeCouriersCount",
                width: 160,
                render: count => (
                    <Text strong style={{color: count > 0 ? "#52c41a" : "#ff4d4f"}}>
                        👤 {count || 0}
                    </Text>
                )
            },
            {
                title: "Kuyruktaki Sipariş Sayısı",
                dataIndex: "queuedDeliveryCount",
                key: "queuedDeliveryCount",
                width: 180,
                render: count => (
                    <Text strong style={{color: count > 0 ? "#faad14" : "#52c41a"}}>
                        📦 {count || 0}
                    </Text>
                )
            },
            {
                title: "Kapasite Oranı",
                dataIndex: "capacityRate",
                key: "capacityRate",
                width: 150,
                render: rate => {
                    const rateValue = rate || 0;
                    const color =
                        rateValue > 80 ? "#ff4d4f" : rateValue > 50 ? "#faad14" : "#52c41a";
                    return (
                        <Text strong style={{color}}>
                            📊 {rateValue}
                        </Text>
                    );
                }
            },
            {
                title: "Hesaplama Zamanı",
                dataIndex: "calculationDate",
                key: "calculationDate",
                width: 200,
                render: date => (
                    <Text style={{color: "#666"}}>
                        🕐 {moment(date).format("DD.MM.YYYY HH:mm:ss")}
                    </Text>
                )
            }
        ];

        // Sort subrow by calculationDate desc (newest first)
        const sortedSubrow = record.subrow
            ? [...record.subrow].sort(
                  (a, b) => new Date(b.calculationDate) - new Date(a.calculationDate)
              )
            : [];

        return (
            <ExpandedContainer>
                <ExpandedTitle>Hesaplama Detayları ({sortedSubrow.length} kayıt)</ExpandedTitle>
                <SubTable
                    columns={subColumns}
                    dataSource={sortedSubrow}
                    pagination={false}
                    rowKey={(subRecord, index) => `${record._id}_${index}`}
                    size="small"
                />
            </ExpandedContainer>
        );
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) {
            setPageSize(size);
        }
    };

    const getActiveFilterText = () => {
        const activeFilters = [];
        if (filters.poolId) activeFilters.push("Havuz");
        if (filters.companyId) activeFilters.push("Firma");
        if (filters.subCompanyId) activeFilters.push("Alt Firma");

        return activeFilters.length > 0
            ? `Aktif Filtreler: ${activeFilters.join(", ")}`
            : "Filtre seçimi yapınız";
    };

    return useObserver(() => (
        <PageContainer>
            <FiltersCard
                title="🔍 Filtreler"
                extra={
                    <Text style={{color: "#666", fontSize: "14px"}}>{getActiveFilterText()}</Text>
                }
            >
                <Row gutter={[16, 16]}>
                    <PoolIntensityCallbacksFilters
                        setCurrentPage={setCurrentPage}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Row>
            </FiltersCard>

            <ContentCard
                title="🔔 Havuz Yoğunluk Bildirimleri"
                extra={
                    hasValidFilters && (
                        <Text style={{color: "white", fontSize: "14px"}}>
                            {poolIntensityData?.pagination?.total || 0} kayıt
                        </Text>
                    )
                }
            >
                {!hasValidFilters ? (
                    <div style={{textAlign: "center", padding: "60px 20px", color: "#999"}}>
                        <div style={{fontSize: "48px", marginBottom: "16px"}}>🔍</div>
                        <div style={{fontSize: "18px", fontWeight: "600", marginBottom: "8px"}}>
                            Filtre Seçimi Gerekli
                        </div>
                        <div style={{fontSize: "14px"}}>
                            Verileri görüntülemek için yukarıdan en az bir filtre seçiniz
                        </div>
                    </div>
                ) : (
                    <MainTable
                        loading={loading}
                        dataSource={poolIntensityData?.data || []}
                        rowKey="_id"
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: poolIntensityData?.pagination?.total || 0,
                            showSizeChanger: false,
                            showQuickJumper: false,
                            showTotal: false,
                            onChange: handlePageChange
                        }}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                        expandable={{
                            expandedRowRender: expandedRowRender,
                            rowExpandable: record => record.subrow && record.subrow.length > 0,
                            expandIcon: ({expanded, onExpand, record}) => (
                                <span
                                    className={`ant-table-row-expand-icon ${
                                        expanded ? "ant-table-row-expand-icon-expanded" : ""
                                    }`}
                                    onClick={e => onExpand(record, e)}
                                    style={{
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    {expanded ? "➖" : "✚"}
                                </span>
                            )
                        }}
                        scroll={{x: "max-content"}}
                        size="middle"
                    >
                        <Column
                            title="📍 Havuz"
                            dataIndex="poolName"
                            key="poolName"
                            width={200}
                            render={poolName => (
                                <Text strong style={{color: "#1890ff"}}>
                                    {poolName || "-"}
                                </Text>
                            )}
                        />
                        <Column
                            title="🏢 Firma"
                            dataIndex="companyName"
                            key="companyName"
                            width={180}
                            render={companyName => (
                                <Text strong style={{color: "#52c41a"}}>
                                    {companyName || "-"}
                                </Text>
                            )}
                        />
                        <Column
                            title="🏪 Alt Firma"
                            dataIndex="subCompanyName"
                            key="subCompanyName"
                            width={200}
                            render={subCompanyName => {
                                const raw =
                                    typeof subCompanyName === "string"
                                        ? subCompanyName.trim()
                                        : subCompanyName != null
                                          ? String(subCompanyName).trim()
                                          : "";
                                if (!raw) {
                                    return (
                                        <Text strong style={{color: "#faad14"}}>
                                            -
                                        </Text>
                                    );
                                }
                                const shortened = truncateString(raw, 36);
                                const truncated = shortened.endsWith("...");
                                return (
                                    <Text
                                        strong
                                        style={{
                                            color: "#faad14",
                                            cursor: "pointer",
                                            textDecoration: truncated
                                                ? "underline dotted"
                                                : "underline",
                                            wordBreak: "break-word"
                                        }}
                                        onClick={() =>
                                            setSubCompaniesModal({
                                                open: true,
                                                subCompanyName: raw
                                            })
                                        }
                                    >
                                        {shortened}
                                    </Text>
                                );
                            }}
                        />
                        <Column
                            title="🎯 Yoğunluk Tipi"
                            dataIndex="type"
                            key="type"
                            width={180}
                            render={type => getTypeLabel(type)}
                        />
                        <Column
                            title="⚙️ Konfig Kapasite Oranı"
                            dataIndex="configCapacityRate"
                            key="configCapacityRate"
                            width={200}
                            render={rate => (
                                <Text strong style={{color: "#1890ff"}}>
                                    {rate || 0}
                                </Text>
                            )}
                        />
                        <Column
                            title="⏰ Döngü Zaman Kontrolü"
                            dataIndex="configMinTimeToCheck"
                            key="configMinTimeToCheck"
                            width={200}
                            render={time => <Text style={{color: "#666"}}>{time || 0} dk</Text>}
                        />
                        <Column
                            title="🔄 Deneme Sayısı"
                            dataIndex="retryCount"
                            key="retryCount"
                            width={150}
                            render={count => (
                                <Text strong style={{color: count > 0 ? "#faad14" : "#52c41a"}}>
                                    {count || 0}
                                </Text>
                            )}
                        />
                        <Column
                            title="📅 Gönderilme Zamanı"
                            dataIndex="sentAt"
                            key="sentAt"
                            width={200}
                            render={date => (
                                <Text style={{color: "#666"}}>
                                    {date
                                        ? moment(date).format("DD.MM.YYYY HH:mm:ss")
                                        : "Gönderilmedi"}
                                </Text>
                            )}
                        />
                        <Column
                            title="📊 Hesaplama Sayısı"
                            key="calculationCount"
                            width={160}
                            render={record => {
                                const count = record.subrow ? record.subrow.length : 0;
                                return (
                                    <Tag
                                        color={count > 0 ? "green" : "default"}
                                        style={{fontWeight: 600}}
                                    >
                                        {count} kayıt
                                    </Tag>
                                );
                            }}
                        />
                    </MainTable>
                )}
            </ContentCard>
            <SubCompaniesDetailModal
                visible={subCompaniesModal.open}
                subCompanyName={subCompaniesModal.subCompanyName}
                onCancel={() =>
                    setSubCompaniesModal({open: false, subCompanyName: ""})
                }
            />
        </PageContainer>
    ));
};

export default PoolIntensityCallbacks;
